import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';

import { CustomAlert } from '../../../shared/uiComponents';
import { errorNormalizer } from '../../../shared/Helpers/functions';
import { DispatchProperties } from '../../../redux/store';
import { createRecurringSchedules } from '../../../redux/State/clientSlice/recurringScheduleSlice';
import { CreateRecurringSchedulePayload } from '../../../redux/API/ClientAPIHelpers/recurringSchedule';
import ScheduleForm from './ScheduleForm';
import { buildSchedulePayload, emptyFormValues } from './helpers';
import { AuthBounds, buildValidationSchema } from './validation';
import { ScheduleFormValues } from './types';
import './styles.scss';

interface CreateScheduleModalProperties {
	onClose: () => void;
	onCreated?: () => void;
	open: boolean;
}

const ModalContent: FC<{ onClose: () => void; onCreated?: () => void }> = ({ onClose, onCreated }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [authBounds, setAuthBounds] = useState<AuthBounds>({});
	const validationSchema = useMemo(() => buildValidationSchema(authBounds), [authBounds]);

	const handleSubmit = async (values: ScheduleFormValues) => {
		const schedules: CreateRecurringSchedulePayload[] = buildSchedulePayload(values);

		try {
			const result = await dispatch(createRecurringSchedules(schedules)).then(unwrapResult);

			toast.success(`${result.length} schedule(s) created successfully`);
			onCreated?.();
			onClose();
		} catch (err) {
			const error = err as { errors?: Record<string, unknown>; title?: string; type?: string };

			// Total-auth warning: show confirm dialog and retry with acknowledgment
			if (error.type === 'TotalAuthorizationExceeded') {
				const proceed = window.confirm(
					`${error.title}\n\nDo you want to proceed anyway?`,
				);

				if (proceed) {
					try {
						const acknowledged = schedules.map((s) => ({ ...s, acknowledgeTotalHoursWarning: true }));
						const result = await dispatch(createRecurringSchedules(acknowledged)).then(unwrapResult);

						toast.success(`${result.length} schedule(s) created successfully`);
						onCreated?.();
						onClose();
					} catch (retryErr) {
						errorNormalizer(retryErr as { errors: Record<string, unknown>; title: string });
					}
				}

				return;
			}

			const normalizeOverlapMessage = (value: unknown) => typeof value === 'string'
				? value.replace(
					'An active insurance authorization already exists',
					'An active authorization already exists',
				)
				: value;

			errorNormalizer({
				errors: error.errors
					? Object.fromEntries(Object.entries(error.errors).map(([key, value]) => [key, normalizeOverlapMessage(value)]))
					: error.errors,
				title: normalizeOverlapMessage(error.title) as string,
			});
		}
	};

	return (
		<Formik
			initialValues={emptyFormValues()}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
		>
			{() => (
				<Form>
					<ScheduleForm onAuthBoundsChange={setAuthBounds} onClose={onClose} />
				</Form>
			)}
		</Formik>
	);
};

const CreateScheduleModal: FC<CreateScheduleModalProperties> = ({ open, onClose, onCreated }) => {
	const onCloseRef = useRef(onClose);
	const onCreatedRef = useRef(onCreated);
	useEffect(() => {
		onCloseRef.current = onClose;
		onCreatedRef.current = onCreated;
	}, [onClose, onCreated]);

	const Content = useCallback<FC<{ onClose: () => void }>>(
		() => <ModalContent onClose={() => onCloseRef.current()} onCreated={() => onCreatedRef.current?.()} />,
		[],
	);

	return (
		<CustomAlert
			title="Create New Schedule"
			open={open}
			onClose={onClose}
			maxWidth="sm"
			disableBackdropClose
			Content={Content}
		/>
	);
};

export default CreateScheduleModal;
